import {
  addDoc,
  collection,
  collectionGroup,
  type CollectionReference,
  doc,
  type DocumentReference,
  type DocumentSnapshot,
  type FirestoreDataConverter,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  type Query,
  type QueryDocumentSnapshot,
  type QuerySnapshot,
  setDoc,
  type SnapshotOptions,
  type Unsubscribe,
  where,
  type WithFieldValue,
} from 'firebase/firestore';
import { getFirestoreInstance } from './firestoreClient';

export const firestoreComandaConverter: FirestoreDataConverter<Pagcomanda.Comanda> = {
  toFirestore(comanda: WithFieldValue<Pagcomanda.Comanda>) {
    return comanda;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.Comanda {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Comanda document data is empty');
    }

    return data as Pagcomanda.Comanda;
  },
};

export const firestoreComandaItemConverter: FirestoreDataConverter<Pagcomanda.ComandaItem> = {
  toFirestore(item: WithFieldValue<Pagcomanda.ComandaItem>) {
    return item;
  },
  fromFirestore(
    snapshot: QueryDocumentSnapshot,
    options?: SnapshotOptions,
  ): Pagcomanda.ComandaItem {
    const data = snapshot.data(options);

    if (!data) {
      throw new Error('Comanda item document data is empty');
    }

    return data as Pagcomanda.ComandaItem;
  },
};

export function getComandaCollectionName(
  billType?: Pagcomanda.BillType,
): 'bill-type-p' | 'bills' {
  return billType === 'h' ? 'bills' : 'bill-type-p';
}

export function getComandaCollection(
  billType?: Pagcomanda.BillType,
): CollectionReference<Pagcomanda.Comanda> {
  return collection(
    getFirestoreInstance(),
    getComandaCollectionName(billType),
  ).withConverter(firestoreComandaConverter);
}

export function getComandaRef(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
): DocumentReference<Pagcomanda.Comanda> {
  return doc(
    getFirestoreInstance(),
    getComandaCollectionName(billType),
    billId,
  ).withConverter(firestoreComandaConverter);
}

export function getComandaItemsRef(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
): CollectionReference<Pagcomanda.ComandaItem> {
  return collection(
    getFirestoreInstance(),
    getComandaCollectionName(billType),
    billId,
    'billItems',
  ).withConverter(firestoreComandaItemConverter);
}

export function getBillItemsCollectionGroup(): Query<Pagcomanda.ComandaItem> {
  return collectionGroup(
    getFirestoreInstance(),
    'billItems',
  ).withConverter(firestoreComandaItemConverter);
}

export function getComanda(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
): Promise<DocumentSnapshot<Pagcomanda.Comanda>> {
  return getDoc(getComandaRef(billType, billId));
}

export function getComandaItems(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
): Promise<QuerySnapshot<Pagcomanda.ComandaItem>> {
  return getDocs(query(getComandaItemsRef(billType, billId), orderBy('createdAt', 'asc')));
}

export function observeComanda(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
  onNext: (snapshot: DocumentSnapshot<Pagcomanda.Comanda>) => void,
): Unsubscribe {
  return onSnapshot(getComandaRef(billType, billId), onNext);
}

export function observeComandaItems(
  billType: Pagcomanda.BillType | undefined,
  billId: string,
  onNext: (snapshot: QuerySnapshot<Pagcomanda.ComandaItem>) => void,
): Unsubscribe {
  return onSnapshot(
    query(getComandaItemsRef(billType, billId), orderBy('createdAt', 'asc')),
    onNext,
  );
}

export function addComanda(
  billType: Pagcomanda.BillType,
  data: Partial<WithFieldValue<Pagcomanda.Comanda>>,
): Promise<DocumentReference<Pagcomanda.Comanda>> {
  return addDoc(getComandaCollection(billType), data as WithFieldValue<Pagcomanda.Comanda>);
}

export function saveComandaMetadata(
  comandaRef: DocumentReference<Pagcomanda.Comanda>,
  metadata: Pagcomanda.ComandaMetadata,
): Promise<void> {
  return setDoc(comandaRef, { metadata }, { merge: true });
}

export function getOpenBillsForUser(
  userId: string,
): Promise<QuerySnapshot<Pagcomanda.Comanda>> {
  return getDocs(query(
    getComandaCollection('p'),
    where('userId', '==', userId),
    where('paymentStatus', '==', 0),
    orderBy('createdAt', 'desc'),
  ));
}

export function getUserPaidBills(
  userId: string,
): Promise<QuerySnapshot<Pagcomanda.Comanda>> {
  return getDocs(query(
    getComandaCollection('h'),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc'),
  ));
}

export function getBillsForVenueAndDateRange(
  venueRef: DocumentReference<Pagcomanda.Venue>,
  start: Date,
  end: Date,
): Promise<QuerySnapshot<Pagcomanda.Comanda>> {
  return getDocs(query(
    getComandaCollection('h'),
    where('venue', '==', venueRef),
    where('createdAt', '>=', start),
    where('createdAt', '<=', end),
  ));
}

export function getFinalBillItemsForVenueAndDateRange(
  venueId: string,
  start: Date,
  end: Date,
): Promise<QuerySnapshot<Pagcomanda.ComandaItem>> {
  return getDocs(query(
    getBillItemsCollectionGroup(),
    where('venueId', '==', venueId),
    where('isFinal', '==', true),
    where('createdAt', '>=', start),
    where('createdAt', '<=', end),
  ));
}
